import {
useEffect,
useRef,
useState,
} from "react";

import {
CandlestickSeries,
ColorType,
createChart,
} from "lightweight-charts";

const DEFAULT_WEEX_API_BASE_URL =
"https://api-contract.weex.com";

const HISTORICAL_LIMIT = 100;

const TIMEFRAME =
"5m";

const CHART_HEIGHT =
360;

const normalizeSymbol = (
value
) => {
const normalized =
String(
value ??
"BTCUSDT"
)
.trim()
.toUpperCase();

return normalized || "BTCUSDT";
};

const toUnixSeconds = (
value
) => {
if (
value === undefined ||
value === null ||
value === ""
) {
return null;
}

if (
typeof value === "number" &&
Number.isFinite(value)
) {
return value > 10_000_000_000
? Math.floor(
value / 1000
)
: Math.floor(
value
);
}

/*
 * Normalize object-based time values before they
 * can ever reach Lightweight Charts.
 */
if (
typeof value === "object"
) {
const nestedValue =
value.timestamp ??
value.time ??
value.openTime ??
value.closeTime;

if (
nestedValue !== undefined &&
nestedValue !== value
) {
const nestedTimestamp =
toUnixSeconds(
nestedValue
);

if (
nestedTimestamp !== null
) {
return nestedTimestamp;
}
}

/*
 * Lightweight Charts BusinessDay shape.
 */
if (
Number.isFinite(
Number(value.year)
) &&
Number.isFinite(
Number(value.month)
) &&
Number.isFinite(
Number(value.day)
)
) {
const businessDayTimestamp =
Date.UTC(
Number(value.year),
Number(value.month) - 1,
Number(value.day)
);

if (
Number.isFinite(
businessDayTimestamp
)
) {
return Math.floor(
businessDayTimestamp / 1000
);
}
}

return null;
}

const numeric =
Number(value);

if (
Number.isFinite(
numeric
) &&
numeric > 0
) {
return numeric > 10_000_000_000
? Math.floor(
numeric / 1000
)
: Math.floor(
numeric
);
}

const parsed =
new Date(
value
).getTime();

if (
!Number.isFinite(
parsed
)
) {
return null;
}

return Math.floor(
parsed / 1000
);
};

const toNumber = (
value
) => {
const number =
Number(value);

return Number.isFinite(
number
)
? number
: null;
};

const readCandle = (
payload
) => {
const candidates = [
payload?.payload?.candle,
payload?.candle,
payload?.payload,
payload,
];

for (
const source
of candidates
) {
if (
!source ||
typeof source !==
"object"
) {
continue;
}

/*
 * WEEX historical candles are arrays.
 * Live dashboard candles are normally objects.
 */
if (
Array.isArray(
source
)
) {
if (
source.length < 5
) {
continue;
}

const time =
toUnixSeconds(
source[0]
);

const open =
toNumber(
source[1]
);

const high =
toNumber(
source[2]
);

const low =
toNumber(
source[3]
);

const close =
toNumber(
source[4]
);

if (
time === null ||
open === null ||
high === null ||
low === null ||
close === null
) {
continue;
}

return {
time,
open,
high,
low,
close,
};
}

const time =
toUnixSeconds(
source.time ??
source.openTime ??
source.timestamp ??
source.closeTime
);

const open =
toNumber(
source.open
);

const high =
toNumber(
source.high
);

const low =
toNumber(
source.low
);

const close =
toNumber(
source.close
);

if (
time === null ||
open === null ||
high === null ||
low === null ||
close === null
) {
continue;
}

return {
time,
open,
high,
low,
close,
};
}

return null;
};

const extractCandleSymbol = (
payload
) => {
const candidates = [
payload,
payload?.payload,
payload?.candle,
payload?.payload?.candle,
];

for (
const source
of candidates
) {
if (
!source ||
typeof source !==
"object" ||
Array.isArray(source)
) {
continue;
}

const value =
source.symbol ??
source.instrument ??
source.instId ??
source.contract;

if (
value !== undefined &&
value !== null &&
String(value).trim()
) {
return normalizeSymbol(
value
);
}
}

return null;
};

const normalizeHistoricalCandle = (
row
) => {
if (
!Array.isArray(row) ||
row.length < 5
) {
return null;
}

const time =
toUnixSeconds(
row[0]
);

const open =
toNumber(
row[1]
);

const high =
toNumber(
row[2]
);

const low =
toNumber(
row[3]
);

const close =
toNumber(
row[4]
);

if (
time === null ||
open === null ||
high === null ||
low === null ||
close === null
) {
return null;
}

return {
time,
open,
high,
low,
close,
};
};

const normalizeHistoricalResponse = (
response
) => {
if (
Array.isArray(response)
) {
return response;
}

if (
Array.isArray(
response?.data
)
) {
return response.data;
}

if (
Array.isArray(
response?.result
)
) {
return response.result;
}

return [];
};

const uniqueByTime = (
candles
) => {
const map =
new Map();

for (
const candle
of candles
) {
if (
candle?.time ===
undefined
) {
continue;
}

map.set(
candle.time,
candle
);
}

return Array.from(
map.values()
).sort(
(
left,
right
) =>
left.time -
right.time
);
};

const firstObject = (
...values
) => {
for (
const value
of values
) {
if (
value &&
typeof value ===
"object"
) {
return value;
}
}

return null;
};

const extractCandidatePayload = (
candidate
) => {
return firstObject(
candidate?.payload?.candidate,
candidate?.payload?.setup,
candidate?.payload,
candidate?.candidate,
candidate?.setup,
candidate
);
};

const extractArray = (
source,
keys
) => {
for (
const key
of keys
) {
const value =
source?.[key];

if (
Array.isArray(
value
)
) {
return value;
}
}

return [];
};

const normalizeFvg = (
fvg
) => {
if (
!fvg ||
typeof fvg !==
"object"
) {
return null;
}

const startTime =
toUnixSeconds(
fvg.startTime ??
fvg.openTime ??
fvg.time ??
fvg.createdAt ??
fvg.candleTime
);

const endTime =
toUnixSeconds(
fvg.endTime ??
fvg.closeTime ??
fvg.mitigatedAt ??
fvg.expiredAt
);

const upper =
toNumber(
fvg.top ??
fvg.high ??
fvg.upper ??
fvg.highPrice ??
fvg.ceiling
);

const lower =
toNumber(
fvg.bottom ??
fvg.low ??
fvg.lower ??
fvg.lowPrice ??
fvg.floor
);

if (
startTime === null ||
upper === null ||
lower === null
) {
return null;
}

return {
startTime,
endTime:
endTime ??
null,
top:
Math.max(
upper,
lower
),
bottom:
Math.min(
upper,
lower
),
direction:
String(
fvg.direction ??
fvg.type ??
""
).toUpperCase(),
};
};

const extractFvgs = (
candidate
) => {
const payload =
extractCandidatePayload(
candidate
);

const source =
firstObject(
payload?.fvg,
payload?.FVG,
payload?.fvgZone,
payload?.fvgZones
);

const collection =
Array.isArray(
source
)
? source
: extractArray(
payload,
[
"fvgs",
"FVGs",
"fvgZones",
"fvgObjects",
"fairValueGaps",
]
);

const values =
collection.length > 0
? collection
: source
? [source]
: [];

return values
.map(
normalizeFvg
)
.filter(Boolean);
};

const normalizeStructureLine = (
structure
) => {
if (
!structure ||
typeof structure !==
"object"
) {
return null;
}

const type =
String(
structure.type ??
structure.event ??
structure.structureType ??
structure.kind ??
""
)
.trim()
.toUpperCase();

const isBos =
type === "BOS" ||
type.includes(
"BREAK_OF_STRUCTURE"
) ||
type.includes(
"BREAK OF STRUCTURE"
);

const isChoch =
type === "CHOCH" ||
type === "MSS" ||
type.includes(
"CHANGE_OF_CHARACTER"
) ||
type.includes(
"CHANGE OF CHARACTER"
);

if (
!isBos &&
!isChoch
) {
return null;
}

const time1 =
toUnixSeconds(
structure.startTime ??
structure.fromTime ??
structure.time ??
structure.eventTime
);

const time2 =
toUnixSeconds(
structure.endTime ??
structure.toTime ??
structure.confirmedAt ??
structure.closeTime
);

const price =
toNumber(
structure.price ??
structure.level ??
structure.breakPrice ??
structure.structurePrice
);

if (
time1 === null ||
price === null
) {
return null;
}

return {
type:
isBos
? "BOS"
: "CHOCH",

time1,

time2:
time2 ??
time1,

price1:
price,

price2:
toNumber(
structure.endPrice ??
structure.toPrice ??
structure.targetPrice
) ??
price,
};
};

const extractStructureLines = (
candidate
) => {
const payload =
extractCandidatePayload(
candidate
);

const collection =
extractArray(
payload,
[
"structureEvents",
"marketStructureEvents",
"structure",
"structureLines",
"bosChoch",
"bosChochEvents",
"events",
]
);

const individual = [
payload?.bos,
payload?.BOS,
payload?.choch,
payload?.CHoCH,
payload?.mss,
payload?.MSS,
].filter(Boolean);

return [
...collection,
...individual,
]
.map(
normalizeStructureLine
)
.filter(Boolean);
};

class DashboardOverlayRenderer {
constructor(
primitive
) {
this.primitive =
primitive;
}

draw(
target
) {
const {
chart,
series,
zones,
structureLines,
} = this.primitive;

if (
!chart ||
!series
) {
return;
}

const timeScale =
chart.timeScale();

target.useBitmapCoordinateSpace(
({
context,
horizontalPixelRatio,
verticalPixelRatio,
}) => {
context.save();

for (
const zone
of zones
) {
const startX =
timeScale.timeToCoordinate(
zone.startTime
);

const endX =
timeScale.timeToCoordinate(
zone.endTime ??
zone.startTime
);

const topY =
series.priceToCoordinate(
zone.top
);

const bottomY =
series.priceToCoordinate(
zone.bottom
);

if (
startX === null ||
topY === null ||
bottomY === null
) {
continue;
}

const resolvedEndX =
endX ??
startX;

const x =
Math.min(
startX,
resolvedEndX
);

const width =
Math.max(
2,
Math.abs(
resolvedEndX -
startX
)
);

const y =
Math.min(
topY,
bottomY
);

const height =
Math.max(
1,
Math.abs(
bottomY -
topY
)
);

const bullish =
zone.direction.includes(
"BULL"
) ||
zone.direction ===
"LONG";

context.fillStyle =
bullish
? "rgba(16,185,129,0.14)"
: "rgba(239,68,68,0.14)";

context.fillRect(
x *
horizontalPixelRatio,
y *
verticalPixelRatio,
width *
horizontalPixelRatio,
height *
verticalPixelRatio
);

context.strokeStyle =
bullish
? "rgba(16,185,129,0.55)"
: "rgba(239,68,68,0.55)";

context.lineWidth =
Math.max(
1,
horizontalPixelRatio
);

context.strokeRect(
x *
horizontalPixelRatio,
y *
verticalPixelRatio,
width *
horizontalPixelRatio,
height *
verticalPixelRatio
);
}

for (
const line
of structureLines
) {
const x1 =
timeScale.timeToCoordinate(
line.time1
);

const x2 =
timeScale.timeToCoordinate(
line.time2
);

const y1 =
series.priceToCoordinate(
line.price1
);

const y2 =
series.priceToCoordinate(
line.price2
);

if (
x1 === null ||
x2 === null ||
y1 === null ||
y2 === null
) {
continue;
}

context.beginPath();

context.strokeStyle =
line.type ===
"BOS"
? "rgba(59,130,246,0.9)"
: "rgba(168,85,247,0.9)";

context.lineWidth =
1.5 *
horizontalPixelRatio;

if (
line.type ===
"CHOCH"
) {
context.setLineDash(
[
5 *
horizontalPixelRatio,
4 *
horizontalPixelRatio,
]
);
} else {
context.setLineDash(
[]
);
}

context.moveTo(
x1 *
horizontalPixelRatio,
y1 *
verticalPixelRatio
);

context.lineTo(
x2 *
horizontalPixelRatio,
y2 *
verticalPixelRatio
);

context.stroke();

context.setLineDash(
[]
);
}

context.restore();
}
);
}
}

class DashboardOverlayView {
constructor(
primitive
) {
this.primitive =
primitive;

this.rendererInstance =
new DashboardOverlayRenderer(
primitive
);
}

renderer() {
return this.rendererInstance;
}

zOrder() {
return "bottom";
}
}

class DashboardOverlayPrimitive {
constructor() {
this.chart =
null;

this.series =
null;

this.requestUpdate =
null;

this.zones =
[];

this.structureLines =
[];

this.view =
new DashboardOverlayView(
this
);
}

attached({
chart,
series,
requestUpdate,
}) {
this.chart =
chart;

this.series =
series;

this.requestUpdate =
requestUpdate;
}

detached() {
this.chart =
null;

this.series =
null;

this.requestUpdate =
null;
}

update({
zones,
structureLines,
}) {
this.zones =
zones ?? [];

this.structureLines =
structureLines ?? [];

if (
this.requestUpdate
) {
this.requestUpdate();
}
}

updateAllViews() {
if (
this.requestUpdate
) {
this.requestUpdate();
}
}

paneViews() {
return [
this.view,
];
}
}

export function MarketChart({
candle,
candidate = null,
symbol = "BTCUSDT",
}) {
const containerRef =
useRef(null);

const chartRef =
useRef(null);

const seriesRef =
useRef(null);

const overlayRef =
useRef(null);

/*
 * Keeps the latest live candle available while
 * historical REST data is being loaded.
 */
const latestLiveCandleRef =
useRef(null);

/*
 * Last timestamp actually present in the chart series.
 * update() may only receive the same timestamp or a
 * newer timestamp.
 */
const lastChartTimeRef =
useRef(null);

const [
historicalStatus,
setHistoricalStatus,
] =
useState(
"LOADING"
);

const [
historicalError,
setHistoricalError,
] =
useState(null);

const normalizedSymbol =
normalizeSymbol(
symbol
);

useEffect(() => {
if (
!containerRef.current
) {
return undefined;
}

const chart =
createChart(
containerRef.current,
{
layout: {
background: {
type:
ColorType.Solid,

color:
"#0f172a",
},

textColor:
"#94a3b8",
},

grid: {
vertLines: {
color:
"#1e293b",
},

horzLines: {
color:
"#1e293b",
},
},

rightPriceScale: {
borderColor:
"#334155",
},

timeScale: {
borderColor:
"#334155",

timeVisible:
true,

secondsVisible:
false,

rightOffset:
8,

barSpacing:
7,
},

crosshair: {
vertLine: {
color:
"#475569",

width:
1,

style:
2,
},

horzLine: {
color:
"#475569",

width:
1,

style:
2,
},
},

width:
containerRef
.current
.clientWidth,

height:
CHART_HEIGHT,
}
);

const series =
chart.addSeries(
CandlestickSeries,
{
upColor:
"#10b981",

downColor:
"#ef4444",

borderVisible:
false,

wickUpColor:
"#10b981",

wickDownColor:
"#ef4444",
}
);

const overlay =
new DashboardOverlayPrimitive();

series.attachPrimitive(
overlay
);

chartRef.current =
chart;

seriesRef.current =
series;

overlayRef.current =
overlay;

const resize =
() => {
if (
!containerRef.current ||
!chartRef.current
) {
return;
}

chartRef.current.resize(
containerRef
.current
.clientWidth,

CHART_HEIGHT
);
};

window.addEventListener(
"resize",
resize
);

return () => {
window.removeEventListener(
"resize",
resize
);

if (
seriesRef.current &&
overlayRef.current
) {
try {
seriesRef.current.detachPrimitive(
overlayRef.current
);
} catch {
// Chart cleanup continues.
}
}

overlayRef.current =
null;

seriesRef.current =
null;

chartRef.current =
null;

chart.remove();
};
}, []);

/*
 * IMPORTANT:
 *
 * Historical data is now loaded for the
 * currently displayed symbol instead of
 * being permanently hardcoded to BTCUSDT.
 *
 * The dependency includes normalizedSymbol,
 * so changing BTC → ETH → SOL reloads the
 * correct historical context.
 */
useEffect(() => {
let cancelled =
false;

/*
 * Prevent an old live candle from one symbol
 * from being reused for another symbol.
 */
latestLiveCandleRef.current =
null;

lastChartTimeRef.current =
null;

const loadHistoricalCandles =
async () => {
setHistoricalStatus(
"LOADING"
);

setHistoricalError(
null
);

const baseUrl =
String(
import.meta.env
.VITE_WEEX_CONTRACT_API_BASE_URL ??
DEFAULT_WEEX_API_BASE_URL
)
.trim()
.replace(
/\/+$/,
""
);

const url =
new URL(
"/capi/v3/market/klines",
`${baseUrl}/`
);

/*
 * FIX:
 *
 * Use the actual dashboard symbol.
 */
url.searchParams.set(
"symbol",
normalizedSymbol
);

url.searchParams.set(
"interval",
TIMEFRAME
);

url.searchParams.set(
"limit",
String(
HISTORICAL_LIMIT
)
);

try {
const response =
await fetch(
url.toString(),
{
method:
"GET",

headers: {
Accept:
"application/json",
},

cache:
"no-store",
}
);

if (
!response.ok
) {
throw new Error(
`Historical ${normalizedSymbol} candle request failed: ${response.status} ${response.statusText}`
);
}

const responseBody =
await response.json();

const rows =
normalizeHistoricalResponse(
responseBody
);

const candles =
uniqueByTime(
rows
.map(
normalizeHistoricalCandle
)
.filter(
Boolean
)
);

if (
candles.length ===
0
) {
throw new Error(
`WEEX returned no historical ${normalizedSymbol} ${TIMEFRAME} candles`
);
}

if (
cancelled ||
!seriesRef.current
) {
return;
}

/*
 * Seed the full historical context.
 */
seriesRef.current.setData(
candles
);

/*
 * setData() establishes the authoritative
 * chronological boundary for the chart.
 */
lastChartTimeRef.current =
candles[
candles.length - 1
].time;

/*
 * A live candle can arrive while REST
 * history is loading. Reapply it only if
 * it is still valid relative to the seeded
 * historical series.
 */
const buffered =
latestLiveCandleRef.current;

if (
buffered &&
buffered.symbol ===
normalizedSymbol &&
buffered.candle
) {
const bufferedTime =
buffered.candle.time;

const lastChartTime =
lastChartTimeRef.current;

if (
Number.isInteger(
bufferedTime
) &&
(
lastChartTime === null ||
bufferedTime >=
lastChartTime
)
) {
try {
seriesRef.current.update(
buffered.candle
);

lastChartTimeRef.current =
bufferedTime;
} catch (error) {
console.warn(
"[MarketChart] Ignoring stale buffered candle:",
error
);
}
}
}

latestLiveCandleRef.current =
null;

chartRef.current
?.timeScale()
.fitContent();

setHistoricalStatus(
"READY"
);
} catch (
error
) {
if (
cancelled
) {
return;
}

console.error(
"[MarketChart] Historical candle load failed:",
error
);

setHistoricalError(
error?.message ??
"Historical candle request failed"
);

setHistoricalStatus(
"ERROR"
);
}
};

loadHistoricalCandles();

return () => {
cancelled =
true;
};
}, [
normalizedSymbol,
]);

/*
 * Live candle handling.
 *
 * Only candles belonging to the currently
 * displayed symbol are accepted.
 */
useEffect(() => {
const liveCandle =
readCandle(
candle
);

if (
!liveCandle ||
!seriesRef.current
) {
return;
}

/*
 * The chart must only receive a primitive Unix
 * timestamp. Never allow an object-valued time to
 * reach Lightweight Charts.
 */
if (
!Number.isInteger(
liveCandle.time
) ||
liveCandle.time <= 0
) {
return;
}

const eventSymbol =
extractCandleSymbol(
candle
);

/*
 * If the event explicitly identifies a symbol,
 * enforce symbol equality.
 *
 * If the backend event does not contain a symbol,
 * retain existing behaviour rather than dropping
 * otherwise valid candle updates.
 */
if (
eventSymbol &&
eventSymbol !==
normalizedSymbol
) {
return;
}

latestLiveCandleRef.current = {
symbol:
eventSymbol ??
normalizedSymbol,

candle:
liveCandle,
};

/*
 * History has not seeded the chart yet.
 * Keep this candle buffered and let the historical
 * loader reconcile it after setData().
 */
const lastChartTime =
lastChartTimeRef.current;

if (
lastChartTime === null
) {
return;
}

/*
 * Same timestamp = update the forming candle.
 * Newer timestamp = append the next candle.
 * Older timestamp = stale/out-of-order, ignore it.
 */
if (
liveCandle.time <
lastChartTime
) {
return;
}

try {
seriesRef.current.update(
liveCandle
);

lastChartTimeRef.current =
liveCandle.time;
} catch (error) {
/*
 * A malformed external event must never turn
 * the historical chart into an ERROR state.
 */
console.warn(
"[MarketChart] Ignoring invalid live candle update:",
error
);
}
}, [
candle,
normalizedSymbol,
]);

useEffect(() => {
const zones =
extractFvgs(
candidate
);

const structureLines =
extractStructureLines(
candidate
);

overlayRef.current?.update(
{
zones,
structureLines,
}
);
}, [
candidate,
]);

return (
<div className="rounded-2xl border border-slate-800 bg-slate-900/70 p-4 shadow-xl shadow-black/10">
<div className="mb-4 flex items-center justify-between">
<div>
<div className="flex items-center gap-2">
<p className="text-sm font-semibold text-slate-100">
{normalizedSymbol}
</p>

<span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
5M
</span>
</div>

<p className="text-xs text-slate-500">
Historical context + live candle stream
</p>
</div>

<div className="flex items-center gap-2">
<span
className={[
"rounded-full px-3 py-1 text-xs",

historicalStatus ===
"READY"
? "bg-emerald-500/10 text-emerald-400"

: historicalStatus ===
"ERROR"
? "bg-red-500/10 text-red-400"

: "bg-amber-500/10 text-amber-400",
].join(" ")}
>
{historicalStatus ===
"READY"
? "HISTORY READY"

: historicalStatus ===
"ERROR"
? "HISTORY ERROR"

: "LOADING"}
</span>
</div>
</div>

<div
ref={containerRef}
className="w-full overflow-hidden rounded-xl"
style={{
minHeight:
`${CHART_HEIGHT}px`,
}}
/>

{historicalError && (
<div className="mt-3 rounded-lg border border-red-900/50 bg-red-950/20 px-3 py-2 text-xs text-red-300">
{historicalError}
</div>
)}
</div>
);
}

export default MarketChart;